<template>
	<div
		ref="listRef"
		class="relative flex items-center w-full md:w-[calc(20%-20px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 mt-3 md:mt-0 border-b-[1px] border-gray-light md:border-0"
	>
		<div @click="showList = !showList" class="flex flex-col flex-1 w-[calc(100%-30px)] cursor-pointer">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("jobs.searchbar.type.title") }}</span>
			<span
				v-if="selectedTypes.length"
				class="block w-[calc(100%-6px)] whitespace-nowrap overflow-ellipsis overflow-hidden"
			>
				{{ selectedTypes.map((type) => type.name[locale]).join(" , ") }}
			</span>
			<span v-else class="text-dj-gray">{{ placeholder }}</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-if="showList"
			class="absolute p-5 left-0 top-[60px] md:top-[85px] w-max max-w-[100%] md:max-w-[100vw] h-[max] bg-white border border-dj-gray-light rounded-big flex flex-col gap-y-[10px] z-30"
		>
			<BaseCheckbox v-for="(type, index) in types" :key="index" v-model="type.value" @change="onChangeType(type)">
				{{ type.name[locale] }}
			</BaseCheckbox>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, onMounted } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "SearchbarJobsTypes",
	props: {
		modelValue: {
			type: Array as PropType<number[]>,
			required: true,
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue", "loaded"],
	setup(props, { emit }) {
		const { locale } = useI18n();

		const typesData = ref();
		const route = useRoute();

		onMounted(async () => {
			typesData.value = await $fetch(globalConfig.API + "/tags?type[]=job-type&scope[hasAttached]");
			addValuePropToTypes();
			getValuesFromUrl();
			emit("loaded");
		});

		watch(
			() => props.modelValue,
			(value) => {
				if (value.length == 0) {
					selectedTypes.value = [];
					types.value.forEach((type) => {
						type.value = false;
					});
				}
			}
		);

		type TypeItem = { id: number; name: Record<string, string> };
		type TypeItemWithValue = TypeItem & { value: boolean };
		const types = ref<TypeItemWithValue[]>([]);

		function addValuePropToTypes() {
			types.value = typesData.value.data.map((type) => {
				return {
					...type,
					value: false,
				};
			});
		}

		const selectedTypes = ref<TypeItemWithValue[]>([]);

		function onChangeType(type) {
			const index = types.value.findIndex((pred) => pred.name[locale.value] == type.name[locale.value]);
			if (types.value[index].value) {
				selectedTypes.value.push(type);
				emitTypes();
			} else {
				selectedTypes.value = selectedTypes.value.filter(
					(sType) => sType.name[locale.value] != type.name[locale.value]
				);
				emitTypes();
			}
		}

		function getValuesFromUrl() {
			const query = route.query;

			if (query.type) {
				const typesFromUrl = query.type
					.toString()
					.split(",")
					.map((t) => Number.parseInt(t));

				types.value.forEach((type, index) => {
					typesFromUrl.forEach((typeFU) => {
						if (type.id == typeFU) {
							types.value[index].value = true;
							if (selectedTypes.value.filter((type) => type.id == types.value[index].id).length == 0) {
								selectedTypes.value.push(types.value[index]);
							}
						}
					});
				});

				emitTypes();
			}
		}

		function emitTypes() {
			emit(
				"update:modelValue",
				selectedTypes.value.map((type) => type.id)
			);
		}

		const showList = ref(false);
		const listRef = ref(null);

		onClickOutside(listRef, () => {
			showList.value = false;
		});

		function getSelectedTypes() {
			return selectedTypes.value.map((type) => {
				return type.name[locale.value];
			});
		}

		// Check the url parameters and set value if given
		watch(
			() => route.query,
			(value) => {
				if (value && value.type != undefined) {
					addValuePropToTypes();
					getValuesFromUrl();
				}
			}
		);

		return {
			types,
			locale,
			selectedTypes,
			showList,
			listRef,
			onChangeType,
			getSelectedTypes,
		};
	},
});
</script>
